import { Stamp, Bandage, Hand, Droplet, Triangle, Sun, Moon, Sparkle } from 'lucide-react';
import type { RetouchSettings, RetouchSettingsPatch, RetouchVariant } from '../types';

interface RetouchPanelProps {
  settings: RetouchSettings;
  onChange: (settings: RetouchSettingsPatch) => void;
  /** True when a pen / pressure-capable input device is in use.
   *  Hides the pressure toggles when only a mouse is detected,
   *  so users don't see dead controls. */
  showPressureControls?: boolean;
  /** Clone variant source state — null when no source is set.
   *  Drives the "Source: not set" / "Source: (x, y)" status
   *  text in the Clone variant section. */
  cloneSourcePoint: { x: number; y: number } | null;
  /** True when the user clicked "Set Source" and the next canvas
   *  tap is awaiting source-recording. Flips the button label
   *  and gives the user a way to cancel out. */
  cloneArmed: boolean;
  /** Arm the next canvas tap to record source. Bypassable via
   *  Alt-click on desktop or long-press on touch. */
  onSetCloneSource: () => void;
  /** Clear the current source (returns to "not set" state). */
  onClearCloneSource: () => void;
}

/** Variant chip row, in keyboard-cycle order. Single source of
 *  truth for both the visual chip order in the panel and the
 *  Shift+R cycle order in EditorPage (which imports
 *  `RETOUCH_VARIANT_ORDER` derived from this list). */
export const VARIANT_CHIPS: {
  id: RetouchVariant;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { id: 'clone', label: 'Clone', icon: Stamp },
  { id: 'heal', label: 'Heal', icon: Bandage },
  { id: 'smudge', label: 'Smudge', icon: Hand },
  { id: 'blur', label: 'Blur', icon: Droplet },
  { id: 'sharpen', label: 'Sharpen', icon: Triangle },
  { id: 'dodge', label: 'Dodge', icon: Sun },
  { id: 'burn', label: 'Burn', icon: Moon },
  { id: 'sponge', label: 'Sponge', icon: Sparkle },
];

/** Cycle order for the Shift+R keyboard shortcut. Derived from
 *  `VARIANT_CHIPS` so the keyboard cycle and the visual chip
 *  row can never drift out of sync. */
export const RETOUCH_VARIANT_ORDER: readonly RetouchVariant[] = VARIANT_CHIPS.map((c) => c.id);

const SECTION_CLASS = 'flex flex-col gap-2';

/** Retouch tool's properties panel. Single-mode tool with eight
 *  variants surfaced via a chip row at the top — Clone, Heal,
 *  Smudge, Blur, Sharpen, Dodge, Burn, Sponge. The shared brush
 *  base (size / hardness / opacity / flow / smoothing / pressure
 *  toggles) applies to every variant; variant-specific controls
 *  render below the base. */
export function RetouchPanel({
  settings,
  onChange,
  showPressureControls = true,
  cloneSourcePoint,
  cloneArmed,
  onSetCloneSource,
  onClearCloneSource,
}: RetouchPanelProps) {
  return (
    <div className={SECTION_CLASS}>
      {/* Variant chips — 2 rows × 4 columns. Eight variants fit
          comfortably on a narrow viewport without horizontal
          scrolling. Tap to switch; the active variant gets the
          primary tile. */}
      <div className="grid grid-cols-4 gap-1.5">
        {VARIANT_CHIPS.map((chip) => {
          const Icon = chip.icon;
          const active = settings.variant === chip.id;
          return (
            <button
              key={chip.id}
              type="button"
              title={chip.label}
              aria-label={chip.label}
              aria-pressed={active}
              className={`flex flex-col items-center justify-center gap-0.5 h-12 rounded-md transition-colors ${
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-accent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => onChange({ variant: chip.id })}
            >
              <Icon className="h-4 w-4" />
              <span className="text-[10px]">{chip.label}</span>
            </button>
          );
        })}
      </div>

      {/* Clone / Heal source picker — surfaced ABOVE the brush base
          since picking a source is the first step of the workflow.
          The matching SourceSetRow inside CloneControls / HealControls
          is intentionally omitted so it isn't shown twice. */}
      {(settings.variant === 'clone' || settings.variant === 'heal') && (
        <div className="pt-2 border-t border-border space-y-2">
          <SourceSetRow
            cloneSourcePoint={cloneSourcePoint}
            cloneArmed={cloneArmed}
            onSetCloneSource={onSetCloneSource}
            onClearCloneSource={onClearCloneSource}
          />
        </div>
      )}

      {/* Shared brush base — applies to every variant. The exact
          interpretation of size/hardness/opacity/flow varies by
          variant (e.g. Dodge "exposure" multiplies into Flow), but
          the controls themselves are common. */}
      <div className="pt-2 border-t border-border space-y-2">
        <h5 className="text-[10px] uppercase tracking-wider text-muted-foreground">Brush</h5>
        <SliderRow
          label="Size"
          value={settings.size}
          min={1}
          max={500}
          unit="px"
          onChange={(v) => onChange({ size: v })}
        />
        <SliderRow
          label="Hardness"
          value={settings.hardness}
          min={0}
          max={100}
          unit="%"
          onChange={(v) => onChange({ hardness: v })}
        />
        <SliderRow
          label="Opacity"
          value={settings.opacity}
          min={0}
          max={100}
          unit="%"
          onChange={(v) => onChange({ opacity: v })}
        />
        <SliderRow
          label="Flow"
          value={settings.flow}
          min={0}
          max={100}
          unit="%"
          onChange={(v) => onChange({ flow: v })}
        />
        <SliderRow
          label="Smooth"
          value={settings.smoothing}
          min={0}
          max={100}
          unit="%"
          onChange={(v) => onChange({ smoothing: v })}
        />

        {/* Pressure toggles are only wired for Clone / Heal (stylus
            pressure scales the stamp's tip size + opacity). Hidden for
            the other variants until their dab paths read pressure, so a
            toggle never claims an effect it doesn't have. */}
        {showPressureControls && (settings.variant === 'clone' || settings.variant === 'heal') && (
          <div className="flex flex-wrap gap-x-3 gap-y-1 pt-1">
            <PressureToggle
              label="Size"
              checked={settings.pressureSize}
              onChange={(v) => onChange({ pressureSize: v })}
            />
            <PressureToggle
              label="Opacity"
              checked={settings.pressureOpacity}
              onChange={(v) => onChange({ pressureOpacity: v })}
            />
            <PressureToggle
              label="Flow"
              checked={settings.pressureFlow}
              onChange={(v) => onChange({ pressureFlow: v })}
            />
          </div>
        )}
      </div>

      {/* Variant-specific controls. */}
      <div className="pt-2 border-t border-border space-y-2">
        <h5 className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {VARIANT_CHIPS.find((c) => c.id === settings.variant)?.label ?? 'Variant'}
        </h5>
        <VariantControls settings={settings} onChange={onChange} />
      </div>
    </div>
  );
}

function VariantControls({
  settings,
  onChange,
}: {
  settings: RetouchSettings;
  onChange: (settings: RetouchSettingsPatch) => void;
}) {
  switch (settings.variant) {
    case 'clone':
      return <CloneControls settings={settings} onChange={onChange} />;
    case 'heal':
      return <HealControls settings={settings} onChange={onChange} />;
    case 'smudge':
      return (
        <>
          <SliderRow
            label="Strength"
            value={settings.smudge.strength}
            min={0}
            max={100}
            unit="%"
            onChange={(v) => onChange({ smudge: { strength: v } })}
          />
          <ToggleRow
            label="Finger Painting"
            checked={settings.smudge.fingerPainting}
            onChange={(v) => onChange({ smudge: { fingerPainting: v } })}
            hint="Start each stroke with the foreground color."
          />
        </>
      );
    case 'blur':
      return (
        <SliderRow
          label="Strength"
          value={settings.blur.strength}
          min={0}
          max={100}
          unit="%"
          onChange={(v) => onChange({ blur: { strength: v } })}
        />
      );
    case 'sharpen':
      return (
        <SliderRow
          label="Strength"
          value={settings.sharpen.strength}
          min={0}
          max={100}
          unit="%"
          onChange={(v) => onChange({ sharpen: { strength: v } })}
        />
      );
    case 'dodge':
      return (
        <>
          <SelectRow
            label="Range"
            value={settings.dodge.range}
            options={[
              { value: 'shadows', label: 'Shadows' },
              { value: 'midtones', label: 'Midtones' },
              { value: 'highlights', label: 'Highlights' },
            ]}
            onChange={(v) =>
              onChange({
                dodge: { range: v as 'shadows' | 'midtones' | 'highlights' },
              })
            }
          />
          <SliderRow
            label="Exposure"
            value={settings.dodge.exposure}
            min={0}
            max={100}
            unit="%"
            onChange={(v) => onChange({ dodge: { exposure: v } })}
          />
        </>
      );
    case 'burn':
      return (
        <>
          <SelectRow
            label="Range"
            value={settings.burn.range}
            options={[
              { value: 'shadows', label: 'Shadows' },
              { value: 'midtones', label: 'Midtones' },
              { value: 'highlights', label: 'Highlights' },
            ]}
            onChange={(v) =>
              onChange({
                burn: { range: v as 'shadows' | 'midtones' | 'highlights' },
              })
            }
          />
          <SliderRow
            label="Exposure"
            value={settings.burn.exposure}
            min={0}
            max={100}
            unit="%"
            onChange={(v) => onChange({ burn: { exposure: v } })}
          />
        </>
      );
    case 'sponge':
      return (
        <>
          <SelectRow
            label="Mode"
            value={settings.sponge.mode}
            options={[
              { value: 'saturate', label: 'Saturate' },
              { value: 'desaturate', label: 'Desaturate' },
            ]}
            onChange={(v) =>
              onChange({
                sponge: { mode: v as 'saturate' | 'desaturate' },
              })
            }
          />
          <SliderRow
            label="Flow"
            value={settings.sponge.flow}
            min={0}
            max={100}
            unit="%"
            onChange={(v) => onChange({ sponge: { flow: v } })}
          />
        </>
      );
  }
}

function SliderRow({
  label,
  value,
  min,
  max,
  unit,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  unit?: string;
  onChange: (v: number) => void;
}) {
  return (
    <label className="flex items-center gap-2">
      <span className="text-xs w-14 text-muted-foreground shrink-0">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1"
      />
      <span className="text-xs w-12 text-right tabular-nums">
        {value}
        {unit ?? ''}
      </span>
    </label>
  );
}

function SelectRow({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center gap-2">
      <span className="text-xs w-14 text-muted-foreground shrink-0">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 text-xs bg-background border border-border rounded px-2 py-1"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none" title={hint}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4"
      />
      <span className="text-xs">{label}</span>
    </label>
  );
}

function CloneControls({
  settings,
  onChange,
}: {
  settings: RetouchSettings;
  onChange: (settings: RetouchSettingsPatch) => void;
}) {
  return (
    <>
      <SelectRow
        label="Sample"
        value={settings.clone.sample}
        options={[
          { value: 'current', label: 'Current Layer' },
          { value: 'currentBelow', label: 'Current & Below' },
          { value: 'all', label: 'All Layers' },
        ]}
        onChange={(v) =>
          onChange({
            clone: { ...settings.clone, sample: v as 'current' | 'currentBelow' | 'all' },
          })
        }
      />
      <ToggleRow
        label="Aligned"
        checked={settings.clone.aligned}
        onChange={(v) => onChange({ clone: { ...settings.clone, aligned: v } })}
        hint="Keep source point in lockstep with the brush across release / re-stroke."
      />
    </>
  );
}

/** Heal Brush controls. Reuses the source-set workflow with
 *  Clone Stamp via the shared SourceSetRow. Source is hardcoded
 *  to Sampled (the only mode that ships) — pattern source is
 *  out-of-scope until a pattern picker ships. Mode picks
 *  between the additive luminosity-match (`normal`) and a
 *  no-blend stamp (`replace`); the Photoshop blend modes
 *  (multiply / screen / darken / lighten) aren't ported because
 *  they're rarely used in real heal workflows. */
function HealControls({
  settings,
  onChange,
}: {
  settings: RetouchSettings;
  onChange: (settings: RetouchSettingsPatch) => void;
}) {
  return (
    <>
      <SelectRow
        label="Mode"
        value={settings.heal.mode}
        options={[
          { value: 'normal', label: 'Normal — match tone' },
          { value: 'replace', label: 'Replace — no blend' },
        ]}
        onChange={(v) =>
          onChange({
            heal: {
              ...settings.heal,
              mode: v as 'normal' | 'replace',
            },
          })
        }
      />
    </>
  );
}

/** Shared "Set Source" button + status text used by Clone Stamp
 *  and Heal Brush. The button is the primary affordance;
 *  Alt-click on the canvas is the desktop secondary. */
function SourceSetRow({
  cloneSourcePoint,
  cloneArmed,
  onSetCloneSource,
  onClearCloneSource,
}: {
  cloneSourcePoint: { x: number; y: number } | null;
  cloneArmed: boolean;
  onSetCloneSource: () => void;
  onClearCloneSource: () => void;
}) {
  return (
    <>
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          className={`flex-1 px-3 py-1.5 text-xs rounded-md border transition-colors ${
            cloneArmed
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border bg-accent text-foreground hover:bg-accent/80'
          }`}
          onClick={onSetCloneSource}
          title={
            cloneArmed
              ? 'Tap the canvas to set source — click button again to cancel'
              : 'Set source point'
          }
        >
          {cloneArmed ? 'Tap canvas to set source…' : 'Set Source'}
        </button>
        {cloneSourcePoint && (
          <button
            type="button"
            className="px-2 py-1.5 text-xs rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-accent"
            onClick={onClearCloneSource}
            title="Clear source"
            aria-label="Clear source"
          >
            ×
          </button>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground italic">
        {cloneSourcePoint
          ? `Source: (${Math.round(cloneSourcePoint.x)}, ${Math.round(cloneSourcePoint.y)})`
          : 'Source: not set. Tap Set Source above, Alt-click, or long-press the canvas.'}
      </p>
    </>
  );
}

function PressureToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-1.5 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-3.5 h-3.5"
      />
      <span className="text-[11px] text-muted-foreground">Pen → {label}</span>
    </label>
  );
}
